/**
 * Wrapper for localStorage of our application specific storage
 *
 * @export
 * @class EvaStorage
 * @extends {Storage}
 */
export class EvaStorage {
    storageProvider;
    /**
     * Indicates local storage is available or not
     * Stored in a private variable with only a public getter because it can not
     * be modified externally
     *
     * @private
     */
    isLocalStorageAvailable = true;
    /**
     * The in-memory mirror of the localStorage data
     *
     * @private
     * @type {T}
     */
    data = {};
    /**
     * The public getter that indicates localStorage is available
     *
     * @readonly
     */
    get localStorageAvailable() { return this.isLocalStorageAvailable; }
    /**
     * Returns the number of key/value pairs currently present in the list associated with the object.
     * Overridden localStorage method with in-memory fallback
     *
     * @readonly
     */
    get length() {
        return this.isLocalStorageAvailable ? this.storageProvider.length : Object.keys(this.data).length;
    }
    /**
     * Empties the list associated with the object of all key/value pairs, if there are any.
     * Overridden localStorage method with key typings and in-memory fallback
     *
     * @returns
     */
    clear() {
        this.isLocalStorageAvailable ? this.storageProvider.clear() : this.data = {};
    }
    /**
     * Returns the current value associated with the given key, or null if the given key does not exist in the list associated with the object.
     * Overridden localStorage method with key typings and in-memory fallback
     *
     * @param {keyof T} key
     * @returns
     */
    getItem(key) {
        if (this.isLocalStorageAvailable) {
            return this.storageProvider.getItem(key);
        }
        return this.data[key] || null;
    }
    /**
     * Returns the name of the nth key in the list, or null if n is greater than or equal to the number of key/value pairs in the object.
     * Overridden localStorage method with  in-memory fallback
     *
     * @param {number} index
     * @returns
     */
    key(index) {
        return this.isLocalStorageAvailable ? this.storageProvider.key(index) : Object.keys(this.data)[index];
    }
    /**
     * Removes the key/value pair with the given key from the list associated with the object, if a key/value pair with the given key exists.
     * Overridden localStorage method with key typings and in-memory fallback
     *
     * @param {keyof T} key
     * @returns
     */
    removeItem(key) {
        this.isLocalStorageAvailable ? this.storageProvider.removeItem(key) : delete this.data[key];
    }
    /**
     * Sets the value of the pair identified by key to value, creating a new key/value pair if none existed for key previously.
     * Overridden localStorage method with key typings and in-memory fallback
     *
     * @param {keyof T} key
     * @returns
     */
    setItem(key, value) {
        this.isLocalStorageAvailable ? this.storageProvider.setItem(key, value) : this.data[key] = value;
    }
    /**
     * Creates an instance of EvaStorage and checks if localStorage is available
     */
    constructor(storageProvider = globalThis.localStorage) {
        this.storageProvider = storageProvider;
        // Detect if localStorage is available and functional
        //
        try {
            this.isLocalStorageAvailable = !!this.storageProvider;
            if (this.isLocalStorageAvailable) {
                const key = '__check_local_storage_available__';
                this.storageProvider.setItem(key, key);
                this.storageProvider.removeItem(key);
                this.isLocalStorageAvailable = true;
            }
        }
        catch (e) {
            this.isLocalStorageAvailable = false;
        }
    }
}
//# sourceMappingURL=eva-sdk-core-storage.js.map